import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AGENTS_API_URL } from '../services/chatApi';
import axiosInstance from '../../../store/api';
import { useAuthStore } from '../../../store/authStore';

export function useChat() {
  const token = localStorage.getItem('token');
  const { activeStoreId, fetchMyStores, myStores, selectActiveStore } = useAuthStore();
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content:
        'Hola, soy DevsSoft Chat. Puedo consultar tus datos de Inventario y POS Retail (solo lectura). ¿Qué quieres analizar?',
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [linkedApps, setLinkedApps] = useState([]);
  const [error, setError] = useState(null);

  const loadContext = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/ai/query/context');
      setLinkedApps(data?.data?.linked_apps || []);
    } catch (err) {
      setLinkedApps([]);
      setError(err?.response?.data?.message || 'No se pudo cargar el contexto de apps vinculadas');
    }
  }, []);

  useEffect(() => {
    loadContext();
    fetchMyStores?.();
  }, [loadContext, fetchMyStores]);

  const retailLinked = useMemo(
    () => linkedApps.some((a) => a.app_key === 'retail_pos_v1'),
    [linkedApps]
  );

  const sendMessage = async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || loading) return;

    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);
    setError(null);

    try {
      const { data } = await axios.post(
        `${AGENTS_API_URL}/chat`,
        {
          message: trimmed,
          store_id: retailLinked ? activeStoreId : null,
          linked_app_keys: linkedApps.map((a) => a.app_key),
          llm_provider: 'deepseek',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const assistantText = data?.clarification?.required
        ? `${data.message}\n\nOpciones: ${(data.clarification.options || [])
            .map((o) => o.label)
            .join(' · ')}`
        : data.message;

      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: assistantText,
          sources: data.sources || [],
          traceId: data.trace_id,
        },
      ]);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Error consultando el agente';
      setError(msg);
      setMessages((prev) => [
        ...prev,
        { id: `e-${Date.now()}`, role: 'assistant', content: `No pude responder: ${msg}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return {
    messages,
    loading,
    linkedApps,
    error,
    retailLinked,
    myStores,
    activeStoreId,
    selectActiveStore,
    sendMessage,
    reloadContext: loadContext,
  };
}
